import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {type NavigateOptionsWithPreventAutofocus, useNavigate} from '@github-ui/use-navigate'
import {useEffect, useState} from 'react'

interface UseListResultsParams {
  defaultFilterValue?: string
}

interface FetchResultsParams {
  q: string
  page?: number
  sidebarItemId?: string
  focusHeaderAction?: boolean
}

function getNavigateOptions(
  sidebarItemId?: string,
  focusHeaderAction?: boolean,
): NavigateOptionsWithPreventAutofocus | undefined {
  if (!sidebarItemId) return undefined

  if (!focusHeaderAction) return {state: {sidebarItemId}}

  return {
    state: {sidebarItemId, focusHeaderAction: true},
    preventAutofocus: true,
  }
}

/**
 * Hook to manage URL-based list results state for repository search.
 * Handles query parameters, pagination, and navigation for repository lists.
 *
 * @param params Configuration object
 * @param params.defaultFilterValue Default filter value to use when no query param is present
 * @returns An object containing:
 *   - fetchedQuery: The active query (empty string if explicitly cleared)
 *   - queryParam: The raw query parameter from URL (or default value)
 *   - currentPage: The current page number from URL
 *   - fetchResults: Function to navigate to new search results
 *   - locationSidebarItemId: The sidebar item ID saved along with the URL
 */
export function useListResults({defaultFilterValue = ''}: UseListResultsParams) {
  const navigate = useNavigate()
  const location = useLocation()

  const {isBrowser} = useRuntimeEnvironment()

  // Defer reading location.state until after hydration to avoid SSR mismatch
  // (location.state is always undefined on server but may have value on client)
  const locationState = isBrowser ? location.state : undefined
  const locationSidebarItemId = locationState?.sidebarItemId

  const currentParams = new URLSearchParams(location.search)
  const [isEmptyQuery, setIsEmptyQuery] = useState(false)
  const queryParam = currentParams.get('q') || defaultFilterValue
  const fetchedQuery = isEmptyQuery ? '' : queryParam

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- We cannot do this with useSyncedState nor directly from the render
    setIsEmptyQuery(false)
  }, [queryParam])

  const currentPage = Number(currentParams.get('page') || 1)

  function fetchResults({q, page = 1, sidebarItemId = locationSidebarItemId, focusHeaderAction}: FetchResultsParams) {
    if (!q) {
      setIsEmptyQuery(true)
      return
    }

    // Don't reset isEmptyQuery here - let the useEffect handle it when queryParam changes.
    // Resetting it synchronously before navigation causes old results to flash.

    const navigateParams = new URLSearchParams()

    if (q && q !== defaultFilterValue) {
      navigateParams.set('q', q)
    }

    if (page > 1) {
      navigateParams.set('page', page.toString())
    }

    const search = navigateParams.toString()
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      },
      getNavigateOptions(sidebarItemId, focusHeaderAction),
    )
  }

  return {
    fetchedQuery,
    queryParam,
    currentPage,
    fetchResults,
    locationSidebarItemId,
    hasLocationState: !!locationState,
  }
}
